/* assets/klaviyo-cta.js */
(() => {
  // Prevent double attach
  if (window.__BB_CTA_LOADED__) return;
  window.__BB_CTA_LOADED__ = true;

  // ================== Fade-in CSS to inject (for iframes/shadow roots) ==================
  const CLOSE_FADE_STYLE_ID = 'bb-close-fade-style';
  const CLOSE_FADE_CSS = `
@keyframes bbFadeIn { from { opacity: 0 } to { opacity: 1 } }
button.klaviyo-close-form.klaviyo-close-form,
.klaviyo-close-form.needsclick,
button[aria-label="Close teaser"].klaviyo-close-form,
button[aria-label="Close dialog"].klaviyo-close-form {
  animation: bbFadeIn 3.5s ease-in-out both !important;
  will-change: opacity;
}
.klaviyo-close-form svg {
  animation: bbFadeIn 3.5s ease-in-out both !important;
}
`;

  // Helpers to inject the style into any Document or ShadowRoot
  function ensureCloseBtnStyles(root) {
    try {
      // For documents, prefer <head>; for shadow roots, append directly
      const styleHost =
        root.nodeType === 9 /* Document */ ? (root.head || root.documentElement) : root;

      if (!styleHost || styleHost.querySelector?.(`#${CSS.escape(CLOSE_FADE_STYLE_ID)}`)) return;

      const style = root.createElement ? root.createElement('style') : document.createElement('style');
      style.id = CLOSE_FADE_STYLE_ID;
      style.textContent = CLOSE_FADE_CSS;

      styleHost.appendChild(style);
    } catch (e) {
      /* ignore */
    }
  }

  // ================== Targeting & tagging ==================
  const COUPON_CANDIDATES = [
    'button[aria-label="Copy coupon code"]',
    'button[aria-label*="coupon" i]',
    'button[title*="coupon" i]',
    '[data-action="copy-coupon"]',
    '[data-testid*="coupon" i]'
  ];
  const CTA_SELECTOR = ['button.bb-cta', ...COUPON_CANDIDATES].join(',');

  const log = (...a) => {
    if (location.search.includes('bbdebug')) console.debug('[bb-cta]', ...a);
  };

  // Add .bb-cta to any candidate elements in this root
  function tagCTAsIn(root) {
    let added = 0;

    // Tag coupon/copy buttons
    COUPON_CANDIDATES.forEach((sel) => {
      root.querySelectorAll?.(sel)?.forEach((el) => {
        if (!el.classList.contains('bb-cta')) {
          el.classList.add('bb-cta');
          added++;
        }
      });
    });

    if (added) log('tagged', added, 'button(s)');

    // Recurse into open shadow roots
    root.querySelectorAll?.('*')?.forEach((el) => {
      if (el.shadowRoot) {
        try {
          ensureCloseBtnStyles(el.shadowRoot);
          tagCTAsIn(el.shadowRoot);
        } catch {}
      }
    });

    // Recurse into same-origin iframes
    root.querySelectorAll?.('iframe')?.forEach((ifr) => {
      try {
        const doc = ifr.contentDocument;
        if (doc && doc.documentElement) {
          ensureCloseBtnStyles(doc);
          tagCTAsIn(doc);
        }
      } catch {
        // cross-origin -> skip
      }
    });
  }

  // ================== Click behavior (optional close helpers) ==================
  function closeRelatedPopup(btn, root) {
    const scope = root || document;

    const popup =
      btn.closest?.('form[data-testid^="klaviyo-form"]') ||
      btn.closest?.('[data-testid="POPUP"]') ||
      btn.closest?.('.klaviyo-form') ||
      btn.closest?.('.kl-private-reset-css-Xuajs1[style*="position: fixed"]');

    let closeBtn = popup?.querySelector?.('.klaviyo-close-form');
    if (closeBtn) {
      setTimeout(() => closeBtn.click(), 2000);
      return;
    }

    closeBtn = (btn.closest?.('.kl-private-reset-css-Xuajs1') || scope).querySelector?.('.klaviyo-close-form');
    if (closeBtn) {
      setTimeout(() => closeBtn.click(), 1000);
      return;
    }

    const fixedWrap = btn.closest?.('.kl-private-reset-css-Xuajs1[style*="position: fixed"]');
    if (fixedWrap) setTimeout(() => { fixedWrap.style.display = 'none'; }, 1000);
  }

  function handleClickFactory(root) {
    return function handleClick(e) {
      const first = e.composedPath ? e.composedPath()[0] : e.target;
      const btn = first?.closest ? first.closest(CTA_SELECTOR) : null;
      if (!btn) return;
      // You can comment this out if you don't want auto-close behavior:
      closeRelatedPopup(btn, root);
    };
  }

  // ================== Observe DOM for dynamic content ==================
  function observeRoot(root) {
    // Initial pass
    tagCTAsIn(root);
    ensureCloseBtnStyles(root);

    const mo = new MutationObserver((muts) => {
      // Only re-scan when nodes get added (perf)
      for (const m of muts) {
        if (m.addedNodes && m.addedNodes.length) {
          tagCTAsIn(root);
          break;
        }
      }
    });
    mo.observe(root, { childList: true, subtree: true });
    return mo;
  }

  function bindClicks(root) {
    const handler = handleClickFactory(root);
    root.addEventListener('click', handler, true);
    return () => root.removeEventListener('click', handler, true);
  }

  // For documents: also attach into same-origin iframes as they appear
  function attachIframeWatcher(doc) {
    const watchIframes = new MutationObserver(() => {
      try {
        doc.querySelectorAll('iframe').forEach((ifr) => {
          try {
            const idoc = ifr.contentDocument;
            if (idoc && !idoc.__bbCtaAttached) {
              idoc.__bbCtaAttached = true;
              bootIn(idoc);
            }
          } catch {}
        });
      } catch {}
    });
    watchIframes.observe(doc, { childList: true, subtree: true });

    // Initial pass
    try {
      doc.querySelectorAll('iframe').forEach((ifr) => {
        try {
          const idoc = ifr.contentDocument;
          if (idoc && !idoc.__bbCtaAttached) {
            idoc.__bbCtaAttached = true;
            bootIn(idoc);
          }
        } catch {}
      });
    } catch {}

    return () => watchIframes.disconnect();
  }

  function bootIn(rootDoc) {
    // Inject close-button fade styles for this context
    ensureCloseBtnStyles(rootDoc);

    // Observe this context
    const mo = observeRoot(rootDoc);

    // Bind clicks in this context
    const offClick = bindClicks(rootDoc);

    // Retry burst for very slow injects (Klaviyo/Replo)
    let tries = 0;
    const tick = rootDoc.defaultView?.setInterval
      ? rootDoc.defaultView.setInterval(() => {
          tries++;
          tagCTAsIn(rootDoc);
          if (tries >= 10) rootDoc.defaultView.clearInterval(tick);
        }, 1000)
      : setInterval(() => {
          tries++;
          tagCTAsIn(rootDoc);
          if (tries >= 10) clearInterval(tick);
        }, 1000);

    // Watch new iframes (only for real Documents)
    const offIframeWatch = rootDoc.nodeType === 9 ? attachIframeWatcher(rootDoc) : () => {};

    // Cleanup on navigation (Turbo/PJAX)
    rootDoc.defaultView?.addEventListener?.('pagehide', () => {
      try {
        mo.disconnect();
        offClick();
        offIframeWatch();
        if (rootDoc.defaultView?.clearInterval) rootDoc.defaultView.clearInterval(tick);
        else clearInterval(tick);
      } catch {}
    }, { once: true });

    // Shopify Theme Editor section lifecycle
    rootDoc.addEventListener?.('shopify:section:load', () => tagCTAsIn(rootDoc));
    rootDoc.addEventListener?.('shopify:section:select', () => tagCTAsIn(rootDoc));
  }

  // ================== Boot ==================
  function boot() {
    log('boot');
    bootIn(document);
    // In case the page’s own onload injects popups late
    window.addEventListener('load', () => tagCTAsIn(document), { once: true });
    // If Klaviyo emits a custom event (some setups do), re-tag
    window.addEventListener('klaviyoForms', () => tagCTAsIn(document));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    // Included at end of body: safe to boot immediately
    setTimeout(boot, 0);
  }

  // ================== Dev helpers ==================
  window.bbCtaDiag = () => {
    const nodes = [...document.querySelectorAll(CTA_SELECTOR)];
    log('found', nodes.length, 'nodes', nodes);
    return nodes;
  };
  window.bbCtaInjectCSS = () => ensureCloseBtnStyles(document);
})();
